import { AlertTriangle, Zap, FlaskConical, Leaf, Users2, ShieldAlert } from "lucide-react";
import { useState } from "react";

const RISK_ICONS = {
  MECANIQUE: AlertTriangle,
  ELECTRIQUE: Zap,
  CHIMIQUE: FlaskConical,
  ENVIRONNEMENT: Leaf,
  ORGANISATION: Users2,
};

export function EpiPictogram({ epi, size = 40 }) {
  const [imgError, setImgError] = useState(false);
  if (epi?.Pictogramme_URL && !imgError) {
    return (
      <img
        src={epi.Pictogramme_URL}
        alt={epi.Libelle}
        style={{ width: size, height: size }}
        className="rounded-full shrink-0"
        onError={() => setImgError(true)}
      />
    );
  }
  return (
    <div style={{ width: size, height: size }} className="rounded-full bg-sky-600 flex items-center justify-center shrink-0">
      <ShieldAlert className="w-1/2 h-1/2 text-white" />
    </div>
  );
}

export function RiskCategoryIcon({ categorie, size = 22, className = "" }) {
  const Icon = RISK_ICONS[categorie] || AlertTriangle;
  return <Icon style={{ width: size, height: size }} className={`text-amber-600 dark:text-amber-400 shrink-0 ${className}`} />;
}

// Real ISO-style risk pictogram (yellow warning triangle image), falls back to lucide category icon.
export function RiskPictogram({ risque, size = 40, className = "" }) {
  const [imgError, setImgError] = useState(false);
  if (risque?.Pictogramme_URL && !imgError) {
    return (
      <img
        src={risque.Pictogramme_URL}
        alt={risque.Libelle}
        style={{ width: size, height: size }}
        className={`object-contain shrink-0 ${className}`}
        onError={() => setImgError(true)}
        data-testid={`risk-pictogram-img-${risque.Risque_ID || ""}`}
      />
    );
  }
  return (
    <div style={{ width: size, height: size }} className={`flex items-center justify-center shrink-0 ${className}`}>
      <RiskCategoryIcon categorie={risque?.Categorie} size={size * 0.7} />
    </div>
  );
}

// Vehicle pictogram (circular blue badge image), falls back to a generic truck badge.
export function VehiclePictogram({ vehicule, size = 40, className = "" }) {
  const [imgError, setImgError] = useState(false);
  if (vehicule?.Pictogramme_URL && !imgError) {
    return (
      <img
        src={vehicule.Pictogramme_URL}
        alt={vehicule.Nom}
        style={{ width: size, height: size }}
        className={`rounded-full object-cover shrink-0 ${className}`}
        onError={() => setImgError(true)}
        data-testid={`vehicule-pictogram-img-${vehicule.Type_ID || ""}`}
      />
    );
  }
  return (
    <div style={{ width: size, height: size }} className={`rounded-full bg-slate-500 flex items-center justify-center shrink-0 ${className}`}>
      <ShieldAlert className="w-1/2 h-1/2 text-white" />
    </div>
  );
}

// Rule pictogram (green circular mandatory-instruction image), falls back to category icon.
export function RulePictogram({ regle, size = 40, className = "" }) {
  const [imgError, setImgError] = useState(false);
  if (regle?.Pictogramme_URL && !imgError) {
    return (
      <img
        src={regle.Pictogramme_URL}
        alt={regle.Libelle}
        style={{ width: size, height: size }}
        className={`rounded-full object-cover shrink-0 ${className}`}
        onError={() => setImgError(true)}
        data-testid={`regle-pictogram-img-${regle.Regle_ID || ""}`}
      />
    );
  }
  const Icon = RISK_ICONS[regle?.Categorie] || AlertTriangle;
  return (
    <div style={{ width: size, height: size }} className={`rounded-full bg-emerald-600 flex items-center justify-center shrink-0 ${className}`}>
      <Icon style={{ width: size * 0.55, height: size * 0.55 }} className="text-white" />
    </div>
  );
}

// Renders a bullet-list section (EPI or RISQUES) as icon + short text rows instead of a text block.
export function PictogramSectionContent({ type, contenu, epiList = [], risquesList = [] }) {
  const lines = (contenu || "").split("\n").filter((l) => l.trim());
  if (lines.length === 0) return null;

  if (type === "EPI") {
    return (
      <div className="flex flex-wrap gap-3">
        {lines.map((line, idx) => {
          const text = line.replace(/^[-•]\s*/, "");
          const match = epiList.find((e) => text.toLowerCase().includes(e.Libelle.toLowerCase()));
          return (
            <div key={idx} className="flex items-center gap-2 border border-slate-200 dark:border-slate-800 rounded-full pl-1.5 pr-4 py-1.5" data-testid={`epi-pictogram-${idx}`}>
              <EpiPictogram epi={match} size={56} />
              <span className="text-sm text-slate-700 dark:text-slate-300">{match?.Libelle || text}</span>
            </div>
          );
        })}
      </div>
    );
  }

  if (type === "RISQUES") {
    return (
      <div className="flex flex-wrap gap-3">
        {lines.map((line, idx) => {
          const text = line.replace(/^[-•]\s*/, "");
          const match = risquesList.find((r) => text.toLowerCase().includes(r.Libelle.toLowerCase()));
          return (
            <div key={idx} className="flex items-center gap-2 border border-slate-200 dark:border-slate-800 rounded-full pl-1.5 pr-4 py-1.5" data-testid={`risque-pictogram-${idx}`}>
              <RiskPictogram risque={match} size={56} />
              <span className="text-sm text-slate-700 dark:text-slate-300">{match?.Libelle || text}</span>
            </div>
          );
        })}
      </div>
    );
  }

  return <p className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap">{contenu}</p>;
}
